import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  FileText,
  PenTool,
  Upload,
  Plus,
  ArrowRight,
  Clock,
  CheckCircle2,
  Loader2,
  FolderOpen
} from "lucide-react";
import { toast } from "sonner";

const getCategoryClass = (category) => {
  const classes = {
    "Contrat": "category-contrat",
    "Facture": "category-facture",
    "CV": "category-cv",
    "Note de service": "category-note",
    "Courrier": "category-courrier",
    "Autre": "category-autre"
  };
  return classes[category] || "category-autre";
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long"
  });
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const firstName = user?.full_name?.split(" ")[0] || "Utilisateur";

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Message - Simple and Friendly */}
      <div className="text-center py-6">
        <h1 className="text-3xl font-bold tracking-tight">
          Bonjour {firstName} !
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Que souhaitez-vous faire aujourd'hui ?
        </p>
      </div>

      {/* Main Actions - Big, Clear Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* Import Document */}
        <Card 
          className="cursor-pointer hover:border-primary hover:shadow-lg transition-all group"
          onClick={() => navigate("/documents")}
        >
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <Upload className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Ajouter un document</h2>
            <p className="text-muted-foreground">
              Importer un fichier PDF dans votre espace
            </p>
          </CardContent>
        </Card>

        {/* Create Signature Request */}
        <Card 
          className="cursor-pointer hover:border-primary hover:shadow-lg transition-all group"
          onClick={() => navigate("/signatures/new")}
        >
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <PenTool className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Faire signer un document</h2>
            <p className="text-muted-foreground">
              Envoyer un document à signer
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Signatures Alert */}
      {stats?.pending_signatures > 0 && (
        <Card className="border-amber-300 bg-amber-50 max-w-3xl mx-auto">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900">
                    {stats.pending_signatures} document{stats.pending_signatures > 1 ? 's' : ''} à signer
                  </h3>
                  <p className="text-amber-700 text-sm">
                    Des documents attendent votre signature
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/signatures")}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="view-pending-btn"
              >
                Voir
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats - Simplified */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <div className="text-3xl font-bold text-primary">{stats?.total_documents || 0}</div>
          <div className="text-sm text-muted-foreground">Documents</div>
        </div>
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <div className="text-3xl font-bold text-amber-600">{stats?.pending_signatures || 0}</div>
          <div className="text-sm text-muted-foreground">À signer</div>
        </div>
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <div className="text-3xl font-bold text-green-600">{stats?.completed_signatures || 0}</div>
          <div className="text-sm text-muted-foreground">Signés</div>
        </div>
        <div className="text-center p-4 rounded-lg bg-muted/50">
          <div className="text-3xl font-bold">{stats?.storage?.disk_free_gb || '—'}</div>
          <div className="text-sm text-muted-foreground">GB libres</div>
        </div>
      </div>

      {/* Recent Documents - Simplified */}
      {stats?.recent_documents?.length > 0 && (
        <Card className="max-w-3xl mx-auto">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Documents récents
              </h3>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/documents">
                  Voir tout
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
            <div className="space-y-3">
              {stats.recent_documents.slice(0, 3).map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/documents?category=${encodeURIComponent(doc.category)}`)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-primary" />
                    <div>
                      <p className="font-medium">{doc.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(doc.created_at)}
                      </p>
                    </div>
                  </div>
                  <Badge className={getCategoryClass(doc.category)}>
                    {doc.category}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories - Clickable, Simplified */}
      {stats?.category_distribution && Object.values(stats.category_distribution).some(v => v > 0) && (
        <Card className="max-w-3xl mx-auto">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 text-center">Vos documents par type</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {Object.entries(stats.category_distribution).map(([category, count]) => (
                <button
                  key={category}
                  onClick={() => count > 0 && navigate(`/documents?category=${encodeURIComponent(category)}`)}
                  disabled={count === 0}
                  className={`text-center p-3 rounded-lg transition-all ${
                    count > 0 
                      ? 'bg-muted/50 hover:bg-muted cursor-pointer hover:scale-105' 
                      : 'bg-muted/20 opacity-50 cursor-not-allowed'
                  }`}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="text-xl font-bold">{count}</div>
                  <div className="text-xs text-muted-foreground truncate">{category}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
